import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { AlertTriangle, CheckCircle2, Calculator } from 'lucide-react'
import type { EconomyBreakdown } from '@/types/economy'
import type { ValidationError } from '@/lib/economy'

interface OwlCalculatorProps {
  breakdown: EconomyBreakdown
  validationErrors: ValidationError[]
}

export function OwlCalculator({ breakdown, validationErrors }: OwlCalculatorProps) {
  const isValid = validationErrors.length === 0
  const totalAllocated = breakdown.segments.reduce((sum, s) => sum + s.owls, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          Payout Breakdown
          {isValid ? (
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Valid
            </Badge>
          ) : (
            <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {validationErrors.length} issue{validationErrors.length !== 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary Row */}
        <div className="grid grid-cols-3 gap-3">
          <SummaryCard label="Total Pot" value={`${breakdown.totalPot}`} unit="owls" />
          <SummaryCard label="Buy-in" value={`${breakdown.buyIn}`} unit="owls/player" />
          <SummaryCard label="Players" value={`${breakdown.playerCount}`} />
        </div>

        {/* Segment Breakdown */}
        {breakdown.segments.map((segment) => (
          <div
            key={segment.key}
            className="rounded-lg bg-stone-50 dark:bg-stone-900 p-3 space-y-2"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{segment.label}</span>
              <span className="text-sm font-semibold tabular-nums">
                {segment.owls} owls <span className="text-stone-400 font-normal">({segment.percentage}%)</span>
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {segment.ranks.map((rank) => (
                <div
                  key={rank.place}
                  className="rounded bg-white dark:bg-stone-800 p-2 text-center"
                >
                  <div className="text-xs text-stone-500">
                    {rank.place === 1 ? '1st' : rank.place === 2 ? '2nd' : '3rd'}
                  </div>
                  <div className="text-sm font-bold tabular-nums">{rank.owls}</div>
                  <div className="text-xs text-stone-400">{rank.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Sum Validation */}
        <div className="flex items-center justify-between text-xs text-stone-500 pt-2 border-t border-stone-200 dark:border-stone-700">
          <span>Total allocated across segments</span>
          <span className={`font-medium ${totalAllocated === breakdown.totalPot ? 'text-emerald-600' : 'text-red-600'}`}>
            {totalAllocated} / {breakdown.totalPot} owls
          </span>
        </div>
      </CardContent>
    </Card>
  )
}

function SummaryCard({
  label,
  value,
  unit,
}: {
  label: string
  value: string
  unit?: string
}) {
  return (
    <div className="rounded-lg bg-stone-50 dark:bg-stone-900 p-3 text-center">
      <div className="text-xs text-stone-500 uppercase tracking-wider">{label}</div>
      <div className="text-lg font-bold tabular-nums mt-1">{value}</div>
      {unit && <div className="text-xs text-stone-400">{unit}</div>}
    </div>
  )
}
